import { RFSData, useRFSStatusUpdate } from "@/api/rfs";
import { formatDateWithIntl } from "@/utils/helper/formatDate";
import { swalErrorDefault } from "@/utils/lib/swal";

type prefixType = "opm" | "gpon" | "evidenceNn";

interface PrefixFieldMapping {
  status: keyof RFSData;
  isSubmitted: keyof RFSData;
  completedDate: keyof RFSData;
  uncompletedDate: keyof RFSData;
}

const PREFIX_FIELD_MAPPINGS: PrefixFieldMapping[] = [
  {
    status: "opmStatus",
    isSubmitted: "opmIsSubmitted",
    completedDate: "opmCompletedDate",
    uncompletedDate: "opmUncompletedDate",
  },
  {
    status: "gponStatus",
    isSubmitted: "gponIsSubmitted",
    completedDate: "gponCompletedDate",
    uncompletedDate: "gponUncompletedDate",
  },
  {
    status: "evidenceNnStatus",
    isSubmitted: "evidenceNnIsSubmitted",
    completedDate: "evidenceNnCompletedDate",
    uncompletedDate: "evidenceNnUncompletedDate",
  },
];

interface DocumentFieldMapping {
  status: keyof RFSData;
  completedDate: keyof RFSData;
  uncompletedDate: keyof RFSData;
}

// New types for document-specific status updates
export type documentType =
  | "abdFromKmz"
  | "abdFromDwg"
  | "abd"
  | "evidenceFeeder"
  | "bakp"
  | "cmld"
  | "clampHook"
  | "evidenceOlt";

// Mapping configuration for database fields to API keys
const FIELD_MAPPING: Record<documentType, DocumentFieldMapping> = {
  abdFromKmz: {
    status: "abdFromKmzStatus",
    completedDate: "abdFromKmzCompletedDate",
    uncompletedDate: "abdFromKmzUncompletedDate",
  },
  abdFromDwg: {
    status: "abdFromDwgStatus",
    completedDate: "abdFromDwgCompletedDate",
    uncompletedDate: "abdFromDwgUncompletedDate",
  },
  abd: {
    status: "abdStatus",
    completedDate: "abdCompletedDate",
    uncompletedDate: "abdUncompletedDate",
  },
  evidenceFeeder: {
    status: "evidenceFeederStatus",
    completedDate: "evidenceFeederCompletedDate",
    uncompletedDate: "evidenceFeederUncompletedDate",
  },
  bakp: {
    status: "bakpStatus",
    completedDate: "bakpCompletedDate",
    uncompletedDate: "bakpUncompletedDate",
  },
  cmld: {
    status: "cmldStatus",
    completedDate: "cmldCompletedDate",
    uncompletedDate: "cmldUncompletedDate",
  },
  clampHook: {
    status: "clampHookStatus",
    completedDate: "clampHookCompletedDate",
    uncompletedDate: "clampHookUncompletedDate",
  },
  evidenceOlt: {
    status: "evidenceOltStatus",
    completedDate: "evidenceOltCompletedDate",
    uncompletedDate: "evidenceOltUncompletedDate",
  },
};

export interface RFSStatusUpdateData {
  [key: string]: string | boolean | null | undefined;
  opmStatus?: string | null;
  opmIsSubmitted?: boolean;
  opmCompletedDate?: string | null;
  opmUncompletedDate?: string | null;
  evidenceNnStatus?: string | null;
  evidenceNnIsSubmitted?: boolean;
  evidenceNnCompletedDate?: string | null;
  evidenceNnUncompletedDate?: string | null;
  gponStatus?: string | null;
  gponIsSubmitted?: boolean;
  gponCompletedDate?: string | null;
  gponUncompletedDate?: string | null;
  // Document-specific fields based on mapping
  abdFromKmzStatus?: string | null;
  abdFromKmzCompletedDate?: string | null;
  abdFromKmzUncompletedDate?: string | null;
  abdFromDwgStatus?: string | null;
  abdFromDwgCompletedDate?: string | null;
  abdFromDwgUncompletedDate?: string | null;
  abdStatus?: string | null;
  abdCompletedDate?: string | null;
  abdUncompletedDate?: string | null;
  evidenceFeederStatus?: string | null;
  evidenceFeederCompletedDate?: string | null;
  evidenceFeederUncompletedDate?: string | null;
  bakpStatus?: string | null;
  bakpCompletedDate?: string | null;
  bakpUncompletedDate?: string | null;
  cmldStatus?: string | null;
  cmldCompletedDate?: string | null;
  cmldUncompletedDate?: string | null;
  clampHookStatus?: string | null;
  clampHookCompletedDate?: string | null;
  clampHookUncompletedDate?: string | null;
  evidenceOltStatus?: string | null;
  evidenceOltCompletedDate?: string | null;
  evidenceOltUncompletedDate?: string | null;
}

export interface RFSStatusUpdateParams {
  id: string;
  withVariable?: "true" | "false";
}

export interface RFSStatusUpdaterOptions {
  onError?: () => void;
}

export interface CreateRFSStatusUpdaterParams {
  rfsId: string;
  overallStatus: string;
  rfsData?: RFSData | null;
  updateStatusMutation: (
    args: {
      data: RFSStatusUpdateData;
      params: RFSStatusUpdateParams;
      build?: (latest: RFSData) => RFSStatusUpdateData;
    },
    options?: {
      onError?: () => void;
    },
  ) => void;
  options?: RFSStatusUpdaterOptions;
  /** The prefix to update (e.g., 'opm', 'gpon', 'evidenceNn') */
  prefix: prefixType;
}

/**
 * Converts ISO date string to custom format in UTC
 * @param dateString ISO date string (e.g., "2026-01-21T14:03:03.495+00:00")
 * @returns Formatted date string in UTC (e.g., "2026-01-21 14:03:03.495") or null
 */
function convertDateFormat(dateString?: string | null): string | null {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");
    const milliseconds = String(date.getUTCMilliseconds()).padStart(3, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  } catch (error) {
    console.warn("Failed to convert date format:", dateString, error);
    return dateString;
  }
}

function assignMappedField(
  source: RFSData,
  target: RFSStatusUpdateData,
  sourceKey: keyof RFSData,
  targetKey: keyof RFSStatusUpdateData,
  isDateField = false,
) {
  const value = source[sourceKey];
  if (value === undefined) return;

  target[targetKey] = isDateField
    ? convertDateFormat(value as string | null | undefined)
    : (value as string | boolean | null);
}

/**
 * Maps RFS data to status update data format
 * Extracts all relevant fields from rfsData and formats them for the API
 * @param rfsData Current RFS data
 * @returns Formatted status update data with all existing fields
 */
export function mapRFSDataToStatusUpdateData(
  rfsData?: RFSData | null,
): RFSStatusUpdateData {
  if (!rfsData) {
    return {};
  }

  const statusData: RFSStatusUpdateData = {};

  for (const mapping of PREFIX_FIELD_MAPPINGS) {
    assignMappedField(
      rfsData,
      statusData,
      mapping.status,
      mapping.status as keyof RFSStatusUpdateData,
    );
    assignMappedField(
      rfsData,
      statusData,
      mapping.isSubmitted,
      mapping.isSubmitted as keyof RFSStatusUpdateData,
    );
    assignMappedField(
      rfsData,
      statusData,
      mapping.completedDate,
      mapping.completedDate as keyof RFSStatusUpdateData,
      true,
    );
    assignMappedField(
      rfsData,
      statusData,
      mapping.uncompletedDate,
      mapping.uncompletedDate as keyof RFSStatusUpdateData,
      true,
    );
  }

  for (const mapping of Object.values(FIELD_MAPPING)) {
    assignMappedField(
      rfsData,
      statusData,
      mapping.status,
      mapping.status as keyof RFSStatusUpdateData,
    );
    assignMappedField(
      rfsData,
      statusData,
      mapping.completedDate,
      mapping.completedDate as keyof RFSStatusUpdateData,
      true,
    );
    assignMappedField(
      rfsData,
      statusData,
      mapping.uncompletedDate,
      mapping.uncompletedDate as keyof RFSStatusUpdateData,
      true,
    );
  }

  return statusData;
}

/**
 * Creates status update data for a specific prefix while preserving other data
 * @param prefix The prefix to update (e.g., 'opm', 'gpon', 'evidenceNn')
 * @param overallStatus The new status to set
 * @param rfsData Current RFS data to preserve other prefixes
 * @returns Status update data with only the specified prefix updated
 */
export function createStatusDataForPrefix(
  prefix: prefixType,
  overallStatus: string,
  rfsData?: RFSData | null,
): RFSStatusUpdateData {
  const now = new Date();
  const formattedDate = formatDateWithIntl(now);
  const validatedStatuses = ["Validated", "Validation Bypassed", "Optional"];
  const isValidated = validatedStatuses.includes(overallStatus);

  // Start with all existing rfsData mapped to status update format
  const statusData: RFSStatusUpdateData = mapRFSDataToStatusUpdateData(rfsData);

  // Override with new status for the specified prefix
  statusData[`${prefix}Status`] = overallStatus;
  statusData[`${prefix}IsSubmitted`] = true;
  statusData[`${prefix}CompletedDate`] = isValidated ? formattedDate : null;
  statusData[`${prefix}UncompletedDate`] = isValidated ? null : formattedDate;

  return statusData;
}

/**
 * Creates a reusable RFS status updater function
 * @param params Configuration for the status updater
 * @returns A function that updates RFS status
 */
export function createRFSStatusUpdater({
  rfsId,
  overallStatus,
  rfsData,
  updateStatusMutation,
  options = {},
  prefix,
}: CreateRFSStatusUpdaterParams) {
  return function updateRFSStatus() {
    if (!rfsId) {
      console.warn("RFS ID is required for status update");
      return;
    }

    const statusData = createStatusDataForPrefix(
      prefix,
      overallStatus,
      rfsData,
    );

    const params: RFSStatusUpdateParams = {
      id: rfsId,
      withVariable: "true",
    };

    updateStatusMutation(
      {
        data: statusData,
        params,
        build: (latest) =>
          createStatusDataForPrefix(prefix, overallStatus, latest),
      },
      {
        onError:
          options.onError ||
          (() => {
            swalErrorDefault.fire({
              title: "Status Update Failed",
              text: "You failed to update the RFS status. Please try again.",
            });
          }),
      },
    );
  };
}

/**
 * Legacy function - creates RFS status update data for OPM
 * @deprecated Use createStatusDataForPrefix('opm', overallStatus, rfsData) instead
 * @param overallStatus The overall status to set
 * @param rfsData Current RFS data
 * @returns Formatted status update data
 */
export function buildRFSStatusUpdateData(
  overallStatus: string,
  rfsData?: RFSData | null,
): RFSStatusUpdateData {
  return createStatusDataForPrefix("opm", overallStatus, rfsData);
}

/**
 * Creates status update data for document-specific fields using the mapping configuration
 * @param documentType The document type to update (e.g., 'abdFromKmz', 'bakp', 'cmld')
 * @param overallStatus The new status to set
 * @returns Status update data with only the specified document fields updated
 */
export function createDocumentStatusData(
  documentType: documentType,
  overallStatus: string,
  rfsData?: RFSData | null,
): RFSStatusUpdateData {
  const now = new Date();
  const formattedDate = formatDateWithIntl(now);
  const isValidated = overallStatus === "Validated";

  const mapping = FIELD_MAPPING[documentType];
  if (!mapping) {
    throw new Error(`Unknown document type: ${documentType}`);
  }

  const statusData: RFSStatusUpdateData = mapRFSDataToStatusUpdateData(rfsData);

  // Set the fields for the specified document type using the mapping
  statusData[mapping.status as keyof RFSStatusUpdateData] = overallStatus;
  statusData[mapping.completedDate as keyof RFSStatusUpdateData] = isValidated
    ? formattedDate
    : null;
  statusData[mapping.uncompletedDate as keyof RFSStatusUpdateData] = isValidated
    ? null
    : formattedDate;

  return statusData;
}

/**
 * Interface for document status updater parameters
 */
export interface CreateDocumentStatusUpdaterParams {
  rfsId: string;
  overallStatus: string;
  documentType: documentType;
  updateStatusMutation: (
    args: {
      data: RFSStatusUpdateData;
      params: RFSStatusUpdateParams;
      build?: (latest: RFSData) => RFSStatusUpdateData;
    },
    options?: {
      onError?: () => void;
    },
  ) => void;
  options?: RFSStatusUpdaterOptions;
}

/**
 * Interface for document status hook parameters
 */
export interface UseDocumentStatusUpdaterParams {
  options?: RFSStatusUpdaterOptions;
}

/**
 * Interface for updateDocumentStatus function parameters
 */
export interface UpdateDocumentStatusParams {
  rfsId: string;
  overallStatus: string;
  documentType: documentType;
  rfsData?: RFSData | null;
}

/**
 * Custom hook for updating document status using React Query
 *
 * This hook provides an easy way to update document status with proper error handling
 * and loading states. It uses the `useRFSStatusUpdate` hook internally.
 *
 * @param params Configuration for the document status updater
 * @returns Object containing update function and mutation states
 *
 * @example
 * ```tsx
 * // In a React component
 * const { updateDocumentStatus, isLoading, isError, error } = useDocumentStatusUpdater({
 *   options: {
 *     onError: () => console.log("Custom error handler")
 *   }
 * });
 *
 * // To trigger the update
 * const handleSubmit = () => {
 *   updateDocumentStatus({
 *     rfsId: "123",
 *     overallStatus: "Validated",
 *     documentType: "bakp"
 *   });
 * };
 *
 * // Use loading state for UI
 * {isLoading && <Spinner />}
 * ```
 */
export function useDocumentStatusUpdater({
  options = {},
}: UseDocumentStatusUpdaterParams = {}) {
  const {
    mutate: updateStatusMutation,
    isPending,
    isError,
    error,
  } = useRFSStatusUpdate();

  const updateDocumentStatus = ({
    rfsId,
    overallStatus,
    documentType,
    rfsData,
  }: UpdateDocumentStatusParams) => {
    if (!rfsId) {
      console.warn("RFS ID is required for status update");
      return;
    }

    const statusData = createDocumentStatusData(
      documentType,
      overallStatus,
      rfsData,
    );

    const params: RFSStatusUpdateParams = {
      id: rfsId,
      withVariable: "true",
    };

    updateStatusMutation(
      {
        data: statusData,
        params,
        build: (latest) =>
          createDocumentStatusData(documentType, overallStatus, latest),
      },
      {
        onError:
          options.onError ||
          (() => {
            swalErrorDefault.fire({
              title: "Status Update Failed",
              text: "You failed to update the document status. Please try again.",
            });
          }),
      },
    );
  };

  return {
    updateDocumentStatus,
    isLoading: isPending,
    isError,
    error,
  };
}

interface ReviewDocumentStatusUpdaterParams {
  rfsId: string;
  problematicDocuments?: Record<string, string | null>;
  rfsData?: RFSData | null;
}

export function useReviewStatusUpdater({
  options = {},
}: UseDocumentStatusUpdaterParams = {}) {
  const {
    mutate: updateStatusMutation,
    isPending,
    isError,
    error,
  } = useRFSStatusUpdate();

  const updateDocumentStatus = ({
    rfsId,
    rfsData,
    problematicDocuments,
  }: ReviewDocumentStatusUpdaterParams) => {
    if (!rfsId) {
      console.warn("RFS ID is required for status update");
      return;
    }

    // Start with all existing rfsData mapped to status update format
    const existingStatusData: RFSStatusUpdateData =
      mapRFSDataToStatusUpdateData(rfsData);

    const statusData: RFSStatusUpdateData = {
      ...existingStatusData,
      ...problematicDocuments,
    };

    const params: RFSStatusUpdateParams = {
      id: rfsId,
      withVariable: "true",
    };

    updateStatusMutation(
      {
        data: statusData,
        params,
        build: (latest) => ({
          ...mapRFSDataToStatusUpdateData(latest),
          ...problematicDocuments,
        }),
      },
      {
        onError:
          options.onError ||
          (() => {
            swalErrorDefault.fire({
              title: "Status Update Failed",
              text: "You failed to update the document status. Please try again.",
            });
          }),
      },
    );
  };

  return {
    updateDocumentStatus,
    isLoading: isPending,
    isError,
    error,
  };
}
